/**
 * Aba "Relatório" da Composição de Saldo.
 *
 * Pedido do usuário (02/10/2026): relatório "tanto de todas as empresas,
 * quanto de empresa ou grupo de empresa filtradas, trazendo a movimentação
 * por período filtrado, o valor de saldo em aberto, o valor já pago, as
 * contas analíticas com mais movimentação, as com mais saldo em aberto",
 * mais os informativos de soma simples, empresa sem lançamento e empresa sem
 * saldo inicial.
 *
 * Lê o que o job em lote gravou de madrugada — não roda o motor, então abre
 * em milissegundos.
 *
 * Toda regra de leitura vive em `relatorio.ts` (função pura, testada). Aqui
 * só renderiza.
 */

import React from 'react';
import { Badge, Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Warning } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import { useAuditoriaInteligente } from '../../../../../hooks/useAuditoriaInteligente';
import {
  IRelatorioComposicao,
  IRelatorioLinha,
  ISemLancamento,
} from '../../../auditoriaInteligente/types';
import { TabelaPorEmpresa } from './TabelaPorEmpresa';
import {
  escalaDaSerie,
  fmtInt,
  fmtValor,
  paramEmpresas,
  pctSemAbertura,
  rotuloConta,
  serieParaGrafico,
  temDados,
  temSaldo,
  textoInvertidos,
  textoSomaSimples,
} from './relatorio';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  linha: 'rgba(15, 23, 42, 0.06)',
  brand: '#1B88F5',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
  ok: '#059669',
  okBg: 'rgba(5, 150, 105, 0.08)',
  roxo: '#7C3AED',
  roxoBg: 'rgba(124, 58, 237, 0.08)',
} as const;

/** Tabular numbers: alinha coluna de valor sem depender de fonte mono. */
const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

interface Props {
  /**
   * external_id das empresas do recorte. Lista VAZIA = carteira inteira, que
   * é um dos dois modos que o usuário pediu — e nesse modo a rota exige
   * Admin/Desenvolvimento, porque é o agregado de 518 empresas.
   */
  empresas: number[];
  /** Só busca quando a aba está visível: a consulta varre 350 mil itens. */
  ativa: boolean;
  /**
   * Clique numa conta invertida da ressalva: abre a empresa dela já filtrada
   * nas contas com saldo contrário à natureza.
   *
   * Pedido da operação (23/10/2026): a ressalva dizia QUANTAS eram e o
   * contador tinha de abrir conta por conta para achar QUAIS.
   */
  onAbrirInvertida?: (linha: IRelatorioLinha) => void;
}

/** Um número do painel, com o rótulo que explica o que ele NÃO é. */
function Indicador({
  rotulo,
  valor,
  nota,
  cor,
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  cor?: string;
}) {
  return (
    <Box flex="1 1 170px" minW="150px">
      <Text
        fontSize="10px"
        fontWeight="700"
        color={COR.faint}
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb={1}
      >
        {rotulo}
      </Text>
      <Text fontSize="21px" fontWeight="700" color={cor ?? COR.ink} sx={NUM} lineHeight="1.15">
        {valor}
      </Text>
      {nota && (
        <Text fontSize="11px" color={COR.muted} mt={0.5}>
          {nota}
        </Text>
      )}
    </Box>
  );
}

/** Ressalva em destaque. `tom` separa o que é norma do que é falta de dado. */
function Ressalva({
  texto,
  tom = 'warn',
  children,
}: {
  texto: string;
  tom?: 'warn' | 'roxo';
  children?: React.ReactNode;
}) {
  const c = tom === 'roxo' ? COR.roxo : COR.warn;
  const bg = tom === 'roxo' ? COR.roxoBg : COR.warnBg;
  return (
    <Box borderLeftWidth="3px" borderColor={c} bg={bg} px={4} py={3} borderRadius="0 4px 4px 0">
      <Flex gap={2} align="flex-start">
        <Box flexShrink={0} mt="2px">
          <Warning size={15} color={c} weight="fill" />
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="12px" color={COR.ink} lineHeight="1.55">
            {texto}
          </Text>
          {children}
        </Box>
      </Flex>
    </Box>
  );
}

/** Lista de contas de um ranking. `metrica` muda o que vai na direita. */
function Ranking({
  titulo,
  explicacao,
  linhas,
  metrica,
}: {
  titulo: string;
  explicacao: string;
  linhas: IRelatorioLinha[];
  metrica: 'saldo' | 'itens' | 'nao_identificado';
}) {
  if (!linhas.length) return null;
  return (
    <Box flex="1 1 300px" minW="280px">
      <Text fontSize="13px" fontWeight="700" color={COR.ink}>
        {titulo}
      </Text>
      <Text fontSize="11px" color={COR.muted} mb={2}>
        {explicacao}
      </Text>
      <Box borderWidth="1px" borderColor={COR.linha} borderRadius="md" overflow="hidden">
        {linhas.map((l, i) => (
          <Flex
            key={`${l.external_id}-${l.conta}-${metrica}`}
            align="center"
            gap={2}
            px={3}
            py={2}
            borderTopWidth={i ? '1px' : 0}
            borderColor={COR.linha}
          >
            <Box flex={1} minW={0}>
              <Text fontSize="12px" color={COR.ink} isTruncated title={rotuloConta(l)}>
                {rotuloConta(l)}
              </Text>
              <Text fontSize="10px" color={COR.faint}>
                empresa {l.external_id}
              </Text>
            </Box>
            <Text fontSize="12px" fontWeight="700" color={COR.ink} sx={NUM} flexShrink={0}>
              {metrica === 'itens' && fmtInt(l.itens_em_aberto)}
              {metrica === 'saldo' &&
                /* Saldo nulo mostra "—": zero afirmaria conta zerada, que é
                   afirmação diferente de "não há linha de saldo na data". */
                (temSaldo(l) ? `R$ ${fmtValor(l.saldo_razao)}` : '—')}
              {metrica === 'nao_identificado' && `R$ ${fmtValor(l.total_nao_identificado)}`}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}

export function AbaRelatorio({ empresas, ativa, onAbrirInvertida }: Props) {
  const { getComposicaoRelatorio, getComposicaoSemLancamento } = useAuditoriaInteligente();

  // Por empresa x por grupo (matriz + filiais). Estado local: o recorte de
  // empresa ja' vem por prop e a quebra e' so' forma de LER o mesmo payload —
  // nao refaz request.
  const [modoQuebra, setModoQuebra] = React.useState<'empresa' | 'grupo'>('empresa');

  const param = paramEmpresas(empresas);

  const { data, isFetching, error } = useQuery<IRelatorioComposicao>({
    queryKey: ['composicao-relatorio', { empresas: param, limite: 10 }],
    queryFn: getComposicaoRelatorio,
    enabled: ativa,
  });

  /**
   * A lista de empresas sem lançamento é de CARTEIRA (não aceita recorte) e
   * exige gate técnico. Um usuário comum recebe 403 — e isso não é erro de
   * tela: o `retry: false` impede o react-query de insistir, e a seção
   * simplesmente não aparece.
   */
  const { data: semLanc } = useQuery<ISemLancamento>({
    queryKey: ['composicao-sem-lancamento', { limite: 20 }],
    queryFn: getComposicaoSemLancamento,
    enabled: ativa,
    retry: false,
  });

  if (!ativa) return null;

  if (isFetching && !data) {
    return (
      <GlassCard intensity="medium" py={14}>
        <Flex direction="column" align="center" gap={3}>
          <Spinner size="sm" color={COR.brand} />
          <Text fontSize="sm" color={COR.faint}>
            Montando o relatório...
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassCard intensity="medium" p={5}>
        <Ressalva
          texto={
            'Não foi possível carregar o relatório. Para a carteira inteira é ' +
            'preciso perfil Administrador ou Desenvolvimento; com empresas ' +
            'marcadas, apenas as da sua carteira aparecem.'
          }
        />
      </GlassCard>
    );
  }

  /* Lote nunca calculado: mostra o aviso do backend, não uma tela de zeros.
     Zero afirmaria "sem pendência", que é diferente de "não calculei". */
  if (!temDados(data)) {
    return (
      <GlassCard intensity="medium" p={5}>
        <Ressalva
          texto={
            data?.aviso ??
            'A composição em lote ainda não foi calculada para este recorte. O job roda às 03:00.'
          }
        />
      </GlassCard>
    );
  }

  const t = data!.totais!;
  const pontos = serieParaGrafico(data);
  const escala = escalaDaSerie(pontos);
  const invertidos = textoInvertidos(data);
  const somaSimples = textoSomaSimples(data);

  return (
    <Flex direction="column" gap={5}>
      {/* ── Painel: o que o lote cobriu ── */}
      <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
        <Flex align="baseline" gap={2} wrap="wrap" mb={4}>
          <Text fontSize="13px" fontWeight="700" color={COR.ink}>
            {empresas.length
              ? `${fmtInt(empresas.length)} empresa(s) no recorte`
              : 'Carteira inteira'}
          </Text>
          <Text fontSize="11px" color={COR.faint}>
            · posição de {data!.data_posicao}
          </Text>
        </Flex>

        <Flex gap={5} wrap="wrap">
          <Indicador
            rotulo="Contas compostas"
            valor={fmtInt(t.contas)}
            nota={`em ${fmtInt(t.empresas)} empresa(s)`}
          />
          <Indicador
            rotulo="Títulos em aberto"
            valor={`R$ ${fmtValor(t.em_aberto.valor)}`}
            nota={`${fmtInt(t.em_aberto.itens)} título(s) a pagar ou receber`}
            cor={COR.brand}
          />
          <Indicador
            rotulo="Pagamento sem título"
            valor={`R$ ${fmtValor(t.pagamento_maior.valor)}`}
            /* NÃO é "já pago": é baixa que não achou provisão, e o contador
               precisa explicar. Ver ROTULO_TIPO em relatorio.ts. */
            nota={`${fmtInt(t.pagamento_maior.itens)} baixa(s) sem provisão correspondente`}
            cor={COR.warn}
          />
          <Indicador
            rotulo="Não identificado"
            valor={`R$ ${fmtValor(t.nao_identificado)}`}
            nota="valor que o matching não conseguiu atribuir"
            cor={COR.warn}
          />
          <Indicador
            rotulo="Baixa parcial"
            valor={`R$ ${fmtValor(t.baixado_parcial.valor)}`}
            nota={`${fmtInt(t.baixado_parcial.itens)} título(s) quitados em parte`}
            cor={COR.ok}
          />
        </Flex>
      </GlassCard>

      {/* ── Ressalvas ── */}
      {(somaSimples || invertidos || t.sem_abertura > 0) && (
        <Flex direction="column" gap={3}>
          {somaSimples && (
            <Ressalva texto={somaSimples} tom="roxo">
              {!!data!.soma_simples?.reciprocas?.length && (
                <Box mt={2} borderWidth="1px" borderColor={COR.linha} borderRadius="md" bg="white">
                  {data!.soma_simples!.reciprocas.map((r, i) => (
                    <Flex
                      key={`${r.external_id}-${r.conta}`}
                      align="center"
                      gap={2}
                      px={3}
                      py={1.5}
                      borderTopWidth={i ? '1px' : 0}
                      borderColor={COR.linha}
                    >
                      <Text fontSize="11px" color={COR.faint} minW="54px">
                        {r.external_id}
                      </Text>
                      <Text fontSize="11px" color={COR.ink} flex={1} minW={0} isTruncated>
                        [{r.conta}] {r.conta_nome ?? '—'}
                      </Text>
                      <Text fontSize="11px" fontWeight="700" color={COR.ink} sx={NUM}>
                        {r.saldo_conhecido ? `R$ ${fmtValor(r.saldo_razao)}` : 'sem saldo'}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}
            </Ressalva>
          )}

          {invertidos && (
            <Ressalva texto={invertidos}>
              {/* QUAIS contas, não só quantas. O backend manda o `detalhe` já
                  recortado nas que PEDEM conferência (as explicadas pelo
                  saldo de abertura ficam fora) e ordenado por valor. Clicar
                  na linha abre a empresa filtrada nessas contas. */}
              {!!data!.saldos_invertidos?.detalhe?.length && (
                <Box mt={2} borderWidth="1px" borderColor={COR.linha} borderRadius="md" bg="white">
                  {data!.saldos_invertidos!.detalhe.map((l, i) => (
                    <Flex
                      key={`${l.external_id}-${l.conta}`}
                      as={onAbrirInvertida ? 'button' : undefined}
                      type={onAbrirInvertida ? 'button' : undefined}
                      onClick={onAbrirInvertida ? () => onAbrirInvertida(l) : undefined}
                      align="center"
                      gap={2}
                      px={3}
                      py={1.5}
                      w="100%"
                      textAlign="left"
                      borderTopWidth={i ? '1px' : 0}
                      borderColor={COR.linha}
                      _hover={onAbrirInvertida ? { bg: COR.warnBg } : undefined}
                      cursor={onAbrirInvertida ? 'pointer' : 'default'}
                      title={
                        onAbrirInvertida
                          ? `Abrir as contas invertidas da empresa ${l.external_id}`
                          : undefined
                      }
                    >
                      <Text fontSize="11px" color={COR.faint} minW="54px">
                        {l.external_id}
                      </Text>
                      <Text fontSize="11px" color={COR.ink} flex={1} minW={0} isTruncated>
                        {rotuloConta(l)}
                      </Text>
                      <Text fontSize="11px" fontWeight="700" color={COR.ink} sx={NUM}>
                        {temSaldo(l) ? `R$ ${fmtValor(l.saldo_razao)}` : 'sem saldo'}
                      </Text>
                    </Flex>
                  ))}
                  {/* O backend limita o `detalhe` (limite=25): sem este aviso
                      o contador leria as 25 como se fossem todas. */}
                  {(data!.saldos_invertidos!.a_investigar ?? 0) >
                    data!.saldos_invertidos!.detalhe.length && (
                    <Flex px={3} py={1.5} borderTopWidth="1px" borderColor={COR.linha}>
                      <Text fontSize="10px" color={COR.muted}>
                        Mostrando as {data!.saldos_invertidos!.detalhe.length} de maior valor, de{' '}
                        {fmtInt(data!.saldos_invertidos!.a_investigar)} a conferir.
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
            </Ressalva>
          )}

          {t.sem_abertura > 0 && (
            <Ressalva
              texto={
                `${fmtInt(t.sem_abertura)} de ${fmtInt(t.contas)} conta(s) ` +
                `(${pctSemAbertura(data)}%) estão sem saldo de abertura. Sem ele, a ` +
                'composição não sabe o que vinha de antes da base e a lista de ' +
                'títulos em aberto pode estar incompleta: o título antigo não ' +
                'aparece porque sua provisão nunca foi carregada. Falta a ' +
                'configuração contábil ou a carga do saldo inicial.'
              }
            />
          )}
        </Flex>
      )}

      {/* ── Por empresa e por grupo: o pedido de 08/10/2026 ── */}
      <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
        <Flex justify="space-between" align="flex-start" gap={3} mb={1} wrap="wrap">
          <Box>
            <Text fontSize="13px" fontWeight="700" color={COR.ink}>
              Composição por {modoQuebra === 'grupo' ? 'grupo de empresas' : 'empresa'}
            </Text>
            <Text fontSize="11px" color={COR.muted} mt={1} maxW="62ch">
              O saldo total, quanto virou título, quanto já foi pago e quanto não achou par. O{' '}
              <b>% casado</b> desconta o que não tem par por natureza — compensação de cartão e
              transferência entre contas da própria empresa não têm título individual para casar.
            </Text>
          </Box>
          <Flex gap={1} bg="rgba(15, 23, 42, 0.04)" borderRadius="8px" p="3px">
            {(['empresa', 'grupo'] as const).map(m => (
              <Box
                key={m}
                px={3}
                py={1}
                borderRadius="6px"
                fontSize="11px"
                fontWeight="600"
                cursor="pointer"
                bg={modoQuebra === m ? 'white' : 'transparent'}
                color={modoQuebra === m ? COR.ink : COR.muted}
                boxShadow={modoQuebra === m ? '0 1px 2px rgba(15,23,42,0.08)' : undefined}
                onClick={() => setModoQuebra(m)}
              >
                {m === 'empresa' ? 'Por empresa' : 'Por grupo'}
              </Box>
            ))}
          </Flex>
        </Flex>
        <Box mt={4} overflowX="auto">
          <TabelaPorEmpresa
            modo={modoQuebra}
            empresas={data!.por_empresa ?? []}
            grupos={data!.por_grupo ?? []}
          />
        </Box>
      </GlassCard>

      {/* ── Rankings: três critérios que discordam de propósito ── */}
      <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
        <Text fontSize="13px" fontWeight="700" color={COR.ink} mb={1}>
          Onde olhar primeiro
        </Text>
        <Text fontSize="11px" color={COR.muted} mb={4}>
          Os três critérios discordam de propósito: a conta de maior saldo não é a de mais títulos,
          e nenhuma das duas é a de maior valor sem identificação.
        </Text>
        <Flex gap={5} wrap="wrap">
          <Ranking
            titulo="Maior saldo"
            explicacao="O que mais pesa no balanço"
            linhas={data!.rankings?.maior_saldo ?? []}
            metrica="saldo"
          />
          <Ranking
            titulo="Mais títulos em aberto"
            explicacao="Onde há mais documento para conferir"
            linhas={data!.rankings?.mais_itens ?? []}
            metrica="itens"
          />
          <Ranking
            titulo="Maior valor não identificado"
            explicacao="Onde o matching falhou"
            linhas={data!.rankings?.nao_identificado ?? []}
            metrica="nao_identificado"
          />
        </Flex>
      </GlassCard>

      {/* ── Série: envelhecimento por mês de ORIGEM do documento ── */}
      {pontos.length > 0 && (
        <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
          <Text fontSize="13px" fontWeight="700" color={COR.ink} mb={1}>
            Títulos em aberto por mês de origem
          </Text>
          <Text fontSize="11px" color={COR.muted} mb={4}>
            Lê-se como envelhecimento: título com data antiga é o que se cobra primeiro. Mês ausente
            não é mês sem movimento — é mês cujos títulos já foram todos baixados.
          </Text>
          <Flex direction="column" gap={2}>
            {pontos.map(p => (
              <Box key={p.mes}>
                <Flex justify="space-between" align="baseline" gap={3} mb={1}>
                  <Text fontSize="12px" color={COR.ink} sx={NUM}>
                    {p.mes}
                  </Text>
                  <Text fontSize="11px" color={COR.muted} sx={NUM} whiteSpace="nowrap">
                    {fmtInt(p.itens)} título(s) · R$ {fmtValor(p.valor)}
                  </Text>
                </Flex>
                <Box h="6px" bg={COR.linha} borderRadius="3px" overflow="hidden">
                  <Box
                    h="100%"
                    w={`${Math.max(1, (p.itens / escala) * 100).toFixed(1)}%`}
                    bg={COR.brand}
                    borderRadius="3px"
                  />
                </Box>
              </Box>
            ))}
          </Flex>
        </GlassCard>
      )}

      {/* ── Empresas que o lote NÃO pôde cobrir ── */}
      {!!semLanc?.empresas?.length && (
        <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
          <Flex align="baseline" gap={2} wrap="wrap" mb={1}>
            <Text fontSize="13px" fontWeight="700" color={COR.ink}>
              Empresas fora do relatório
            </Text>
            <Badge
              borderRadius="full"
              px={2}
              fontSize="10px"
              textTransform="none"
              bg={COR.warnBg}
              color={COR.warn}
            >
              {fmtInt(semLanc.total)} empresa(s) · R$ {fmtValor(semLanc.saldo_total)}
            </Badge>
          </Flex>
          <Text fontSize="11px" color={COR.muted} mb={3}>
            {semLanc.aviso}
          </Text>
          <Box borderWidth="1px" borderColor={COR.linha} borderRadius="md" overflow="hidden">
            {semLanc.empresas.map((e, i) => (
              <Flex
                key={e.external_id}
                align="center"
                gap={2}
                px={3}
                py={2}
                borderTopWidth={i ? '1px' : 0}
                borderColor={COR.linha}
              >
                <Text fontSize="11px" color={COR.faint} minW="54px">
                  {e.external_id}
                </Text>
                <Text fontSize="12px" color={COR.ink} flex={1} minW={0} isTruncated>
                  {e.nome ?? '—'}
                </Text>
                <Text fontSize="11px" color={COR.muted} sx={NUM} whiteSpace="nowrap">
                  {fmtInt(e.contas)} conta(s)
                </Text>
                <Text
                  fontSize="12px"
                  fontWeight="700"
                  color={COR.ink}
                  sx={NUM}
                  minW="120px"
                  textAlign="right"
                >
                  R$ {fmtValor(e.saldo)}
                </Text>
              </Flex>
            ))}
          </Box>
        </GlassCard>
      )}

      {t.calculado_em && (
        <Text fontSize="10px" color={COR.faint} textAlign="right">
          Calculado pelo job em lote. Última atualização em {t.calculado_em}.
        </Text>
      )}
    </Flex>
  );
}
